import type { ISurveyOption, ISurveyQuestion } from "@/model/survey";
import { SURVEY_QUESTION_TYPE } from "@/constants";
import {
  QuestionLabel,
  QuestionTypeCheckbox,
  QuestionTypeLinearScale,
  QuestionTypeParagraph,
  QuestionTypeRadio,
} from "./survey-question";

const RenderOptions = ({ question }: { question: ISurveyQuestion }) => {
  /**
   * Returns selected radio answer.
   * If it's a custom input, adds "Other:" before the value.
   */
  const getRadioValue = (options: ISurveyOption[]) => {
    // Find the first answered option
    const answer = options.find(({ userAnswer }) =>
      Boolean(userAnswer?.trim()),
    );

    if (!answer) return undefined;

    // Return as-is if matches option, else prefix with "Other:"
    return answer.userAnswer === answer.option
      ? answer.userAnswer
      : `Other:${answer.userAnswer}`;
  };

  // Returns an array of non-empty userAnswer strings from the given options.
  // If the option is marked as `isOther` and the answer is custom (not equal to the option value),
  // it prefixes the answer with "Other: ".
  // Example return: ["Option4", "Other: Custom text"]
  const getCheckboxValue = (options: ISurveyOption[]): string[] => {
    return options.reduce<string[]>((acc, option) => {
      const answer = option.userAnswer?.trim();

      if (answer) {
        if (option.isOther && answer !== option.option.trim()) {
          acc.push(`Other:${answer}`);
        } else {
          acc.push(answer);
        }
      }
      return acc;
    }, []);
  };

  switch (question.type) {
    case SURVEY_QUESTION_TYPE.PARAGRAPH:
      return (
        <QuestionTypeParagraph
          value={question?.options[0]?.userAnswer ?? ""}
          onChange={() => {}}
        />
      );

    case SURVEY_QUESTION_TYPE.CHECKBOX:
      return (
        <QuestionTypeCheckbox
          options={question?.options ?? []}
          value={getCheckboxValue(question?.options)}
          onChange={() => {}}
          isReadOnly
        />
      );

    case SURVEY_QUESTION_TYPE.RADIO:
      return (
        <QuestionTypeRadio
          options={question?.options ?? []}
          value={getRadioValue(question.options) ?? ""}
          onChange={() => {}}
          isReadOnly
        />
      );

    case SURVEY_QUESTION_TYPE.LINEAR_SCALE:
      return (
        <QuestionTypeLinearScale
          options={question?.options ?? []}
          value={getRadioValue(question.options) ?? ""}
          onChange={() => {}}
        />
      );
  }
};

const SurveyResponse = ({ questions }: { questions: ISurveyQuestion[] }) => {
  return (
    <div className="pointer-events-none m-2">
      {questions?.map((question: ISurveyQuestion, index: number) => (
        <div
          id={`question_${question.id + index}`}
          key={`question_${question.id + index}`}
          className="mb-4 mt-3 rounded border p-3"
        >
          <QuestionLabel
            questionNo={index + 1}
            questionText={question?.question}
            isRequired={question?.required}
          />
          <div className="ml-5 mt-3">
            <RenderOptions question={question} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default SurveyResponse;
